import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

// Simplified imports
import Sidebar from '../../components/SideBar/Sidebar';
import Input from '../../components/Inputs/Input/Input';
import Loading from '../../components/Loading/Loading';
import SendInvite from './Modals/SendInvite';
import * as Body from './Styles';
import * as defines from '../../../utils/defines/Defines';
import * as Functions from '../../../utils/functions/Functions';
import { State } from '../../redux/store';

// Icons
import SearchIcon from '../../../assets/icons/Search.svg';
import AddIcon from '../../../assets/icons/add-icon.svg';
import Avatar from '../../../assets/images/avatar.png';

// Types
interface UsuarioProps {
  idEmpresarial: string;
  idUsuario: string;
  fotoUsuario: string | null;
  ddd: string;
  celular: string;
  nomeCompleto: string | null;
  email: string;
  cargo: number;
  encarregadoDados: number;
}

const DefaultUserList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Selectors
  const acessLevel = useSelector((state: State) => state.acessLevel.acessLevel);
  const userType = useSelector((state: State) => state.userType.userType);
  const selectedUnity = useSelector((state: State) => state.selectedUnity.selectedUnity);

  // State management
  const [data, setData] = useState<UsuarioProps[]>([]);
  const [filteredUsers, setFiltered] = useState<UsuarioProps[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalCreateUser, setModalCreateUser] = useState(false);
  const [nomeFilial, setNomeFilial] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  // Helper function to get cargo (role) description
  const getCargo = (cargo: number): string => {
    const cargoMap = {
      [defines.CargoEmpresarial['Administrador']]: 'Administrador',
      [defines.CargoEmpresarial['Padrão']]: 'Padrão'
    };
    return cargoMap[cargo] || 'Cargo Indisponível';
  };

  // Filter users based on search input
  const filterData = (name: string) => {
    if (!name) {
      setFiltered(data);
      return;
    }
    const filtered = data.filter(item => 
      item.nomeCompleto?.toUpperCase().includes(name.toUpperCase())
    );
    setFiltered(filtered);
  };

  // Fetch users and branch information
  useEffect(() => {
    // Check access level
    if (acessLevel !== defines.CargoEmpresarial['Administrador']) {
      navigate('/permissao');
      return;
    }

    setLoading(true);

    // Fetch users
    const fetchUsers = axios.get(
      `${defines.apiURL}/api/EmpresarialCRUD?userTypeRequest=${userType}&idFilial=${Functions.ReplaceSpecialCharacters(selectedUnity)}`,
      { withCredentials: true }
    );

    // Fetch branch info
    const fetchBranchInfo = axios.get(
      `${defines.apiURL}/api/Home?userTypeRequest=${userType}&filialInfo=${Functions.ReplaceSpecialCharacters(selectedUnity)}`,
      { withCredentials: true }
    );

    Promise.all([fetchUsers, fetchBranchInfo])
      .then(([usersResponse, branchResponse]) => {
        // Filter out users with null names
        const filteredUsers = usersResponse.data.content.filter(
          (user: UsuarioProps) => user.nomeCompleto !== null && user.nomeCompleto.trim() !== ''
        );

        const sortedUsers = Functions.SortResponse(filteredUsers, 'nomeCompleto');
        setData(sortedUsers);
        setFiltered(sortedUsers);
        setNomeFilial(branchResponse.data.content.nomeFilial);
        setLoading(false);
      })
      .catch((error) => {
        Functions.ResolveErrors(
          error.response.status,
          error.response.data.code,
          error.response.data.message,
          navigate,
          dispatch,
          error.response.data.content
        );
        setLoading(false);
      });
  }, [dispatch, navigate, userType, selectedUnity, acessLevel]);

  // Navigate to user edit page
  const handleUserClick = (user: UsuarioProps) => {
    navigate('/usuarios-empresarial/editar', { state: { user } });
  };

  return (
    <Body.Container>
      <Sidebar
        type={userType}
        content={defines.ContentSidebar.MANAGE_USERS}
        tituloPagina="Usuários"
      />
      
      <Body.SubTitle style={{ marginTop: '10px' }}>
        Toque em um usuário para visualizar seus detalhes.
      </Body.SubTitle>
      
      <Body.Row style={{ width: '100%', alignItems: 'center', marginTop: '20px' }}>
        <Body.FindContent style={{ width: '100%' }}>
          <Body.Find
            placeholder="Buscar usuário"
            onChange={(e) => filterData(e.target.value)}
            ref={inputRef}
          />
          <Body.Icon
            src={SearchIcon}
            style={{ cursor: 'text' }}
            onClick={() => inputRef.current?.focus()}
          />
        </Body.FindContent>
        
        {acessLevel === defines.CargoEmpresarial['Administrador'] && (
          <Body.HeaderButton onClick={() => setModalCreateUser(true)}>
            <Body.Icon
              src={AddIcon}
              style={{ position: 'relative', top: 0, left: 0 }}
            />
          </Body.HeaderButton>
        )}
      </Body.Row>
      
      <Body.Scroll style={{ marginTop: '20px' }}>
        {filteredUsers.length === 0 ? (
          <Body.Text>Nenhum usuário encontrado</Body.Text>
        ) : (
          filteredUsers.map((user) => (
            <Body.SelectUser
              key={user.idUsuario}
              onClick={() => handleUserClick(user)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Body.Photo src={user.fotoUsuario || Avatar} />
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Body.Text>{user.nomeCompleto}</Body.Text>
                  <Body.NoteText>{getCargo(user.cargo)}</Body.NoteText>
                </div>
              </div>
            </Body.SelectUser>
          ))
        )}
      </Body.Scroll>

      {modalCreateUser && (
        <SendInvite
          toggle={setModalCreateUser}
          nomeFilial={nomeFilial || 'atual'}
          idFilial={selectedUnity}
        />
      )}
      
      {loading && <Loading />}
    </Body.Container>
  );
};

export default DefaultUserList;